import { useMutation as useFaireDirectCreatePendingRelationshipMutation } from "@faire/web-api/api/faire-direct/brandAlias/create-pending-relationship/post-hooks";
import { ICreatePendingFaireDirectRelationshipRequest } from "@faire/web-api/indigofair/brand/direct/ICreatePendingFaireDirectRelationshipRequest";
import { logError } from "@faire/web/common/logging";
import { Path } from "@faire/web/common/Path";
import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  isFaireDirectSalesRepLink,
  isValidFaireDirectPathWithoutQualification,
} from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/VALID_LOGGED_OUT_FAIRE_DIRECT_ROUTES";

import { useFaireDirectBrand } from "../useFaireDirectBrand/useFaireDirectBrand";

export const useFaireDirectCreatePendingRelationship = () => {
  const {
    mutate: createPendingRelationshipMutation,
    isPending: isCreatePendingRelationshipCallPending,
  } = useFaireDirectCreatePendingRelationshipMutation();

  const [
    faireDirectPendingRelationshipBrandToken,
    setFaireDirectPendingRelationshipBrandToken,
  ] = useState<string>("");
  // We only want to call the mutation once in each session, so we need to keep track of whether we've already attempted to create the relationship
  const alreadyAttemptedToCreatePendingRelationship: boolean =
    !!faireDirectPendingRelationshipBrandToken;

  const location = useLocation();
  const { faireDirectBrandAlias, faireDirectBrandToken } =
    useFaireDirectBrand();

  const createPendingRelationship = useCallback(() => {
    if (
      isCreatePendingRelationshipCallPending ||
      alreadyAttemptedToCreatePendingRelationship
    ) {
      return;
    }
    // Only create the pending relationship if the user is coming from a Faire Direct path (contains '/direct' in the url)
    // or if the link have a fdb query param
    // The user could be logged in or logged out
    const path = new Path(location);
    const isFromFaireDirectPath = isValidFaireDirectPathWithoutQualification(
      path.pathname
    );
    const fdbQueryParam = path.searchParams.get("fdb");
    const faireDirectBrandTokenAlias = fdbQueryParam || faireDirectBrandAlias;
    if (
      (!fdbQueryParam && !isFromFaireDirectPath) ||
      !faireDirectBrandTokenAlias
    ) {
      return;
    }
    const salesRepCode = maybeGetSaleRepCodeFromPath(path.pathname);
    const request = ICreatePendingFaireDirectRelationshipRequest.build({
      sales_rep_code: salesRepCode,
      widget_token: path.searchParams.get("widgetToken") || undefined,
      outgoing_email_token: path.searchParams.get("oet") || undefined,
    });

    createPendingRelationshipMutation([faireDirectBrandAlias, request], {
      onSuccess: () => {
        setFaireDirectPendingRelationshipBrandToken(faireDirectBrandToken);
      },
      onError: () => {
        logError(
          `Fail to create pending relationship for brand ${faireDirectBrandToken} on page ${location}`
        );
      },
    });
  }, [
    alreadyAttemptedToCreatePendingRelationship,
    faireDirectBrandAlias,
    faireDirectBrandToken,
    location,
    createPendingRelationshipMutation,
    isCreatePendingRelationshipCallPending,
  ]);

  return {
    createPendingRelationship,
    isCreatePendingRelationshipCallPending,
    alreadyAttemptedToCreatePendingRelationship,
    faireDirectPendingRelationshipBrandToken,
  };
};

const maybeGetSaleRepCodeFromPath = (pathname: string): string | undefined => {
  if (!isFaireDirectSalesRepLink(pathname)) {
    return undefined;
  }
  const parts = pathname.split("/");
  return parts.pop();
};
